import clsx from "clsx";
import { Check } from "lucide-react";
import type { KeyboardEvent } from "react";

import { UnitSymbol } from "../orbats/UnitSymbol";
import { t } from "../../i18n/i18n";
import { STEPS, type StepKey } from "./sheet";
import { enemySidc, STEP_ICONS, STEP_LEADS, STEP_REQUIRED } from "./visuals";
import type { CustomEnemySheet } from "../../api/types";

/** Indicateur d'étapes de l'assistant « ENI personnalisé » : une pastille illustrée par étape, reliée à la suivante
 *  par un trait (bleu une fois l'étape passée, jamais collé aux pastilles), le libellé dessous (sur téléphone,
 *  « Étape 3 sur 6 » et le libellé de l'étape en cours seulement). Onglets au clavier : flèches, Début et Fin. Les
 *  couleurs passent d'un état à l'autre en fondu. */
export function StepRail({
  value,
  onChange,
  locked,
}: {
  value: StepKey;
  onChange: (step: StepKey) => void;
  /** Étapes inaccessibles (nouvel ENI pas encore enregistré : seule l'identité est ouverte). */
  locked: (step: StepKey) => boolean;
}) {
  const index = STEPS.findIndex((s) => s.key === value);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const open = STEPS.map((s) => !locked(s.key));
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    let next = -1;
    if (event.key === "Home") next = open.indexOf(true);
    else if (event.key === "End") next = open.lastIndexOf(true);
    else if (delta) {
      for (let i = index + delta; i >= 0 && i < STEPS.length; i += delta) {
        if (open[i]) {
          next = i;
          break;
        }
      }
    }
    if (next < 0 || next === index) return;
    event.preventDefault();
    onChange(STEPS[next]!.key);
    event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]')[next]?.focus();
  };
  return (
    <div className="rounded-lg border border-line bg-surface px-3 pt-3 pb-2.5 shadow-card">
      <div role="tablist" aria-label={t("Étapes")} className="flex items-start" onKeyDown={onKeyDown}>
        {STEPS.map((s, i) => {
          const Icon = STEP_ICONS[s.key];
          const current = i === index;
          const passed = i < index;
          const disabled = locked(s.key);
          return (
            <div key={s.key} className={clsx("flex min-w-0 items-start", i > 0 && "flex-1")}>
              {i > 0 && (
                <span
                  aria-hidden
                  className={clsx("mx-1.5 mt-3.75 h-0.5 min-w-2 flex-1 rounded-full transition-colors max-sm:mx-1 max-sm:mt-3.25 max-sm:min-w-1", i <= index ? "bg-accent" : "bg-line-strong")}
                />
              )}
              <button
                type="button"
                role="tab"
                aria-selected={current}
                aria-label={t("Étape {n} : {label}", { n: i + 1, label: t(s.label) })}
                tabIndex={current ? 0 : -1}
                disabled={disabled}
                onClick={() => onChange(s.key)}
                className="group flex w-20 shrink-0 flex-col items-center gap-1.5 rounded-lg disabled:opacity-40 max-md:w-16 max-sm:w-7"
              >
                <span
                  className={clsx(
                    "flex size-8 items-center justify-center rounded-full border transition-colors max-sm:size-7",
                    current
                      ? "border-accent bg-accent text-white shadow-chip"
                      : passed
                        ? "border-accent bg-accent-soft text-accent"
                        : "border-line-strong bg-surface text-ink-muted group-hover:bg-hover group-hover:text-ink",
                  )}
                >
                  {passed ? <Check size={15} aria-hidden /> : <Icon size={15} aria-hidden />}
                </span>
                <span
                  className={clsx(
                    "text-center text-meta leading-tight break-words transition-colors max-sm:sr-only",
                    current ? "font-medium text-ink" : "text-ink-muted",
                  )}
                >
                  {t(s.label)}
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-center text-meta text-ink-muted sm:hidden">
        {t("Étape {n} sur {total}", { n: index + 1, total: STEPS.length })} · <span className="font-medium text-ink">{t(STEPS[index]!.label)}</span>
      </p>
    </div>
  );
}

/** Titre d'une étape : pictogramme dans une pastille, intitulé et phrase d'introduction. */
export function StepHeading({ step }: { step: StepKey }) {
  const Icon = STEP_ICONS[step];
  const index = STEPS.findIndex((s) => s.key === step);
  return (
    <div className="flex items-start gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent" aria-hidden>
        <Icon size={18} />
      </span>
      <div className="min-w-0">
        <p className="text-meta leading-snug text-ink-muted tabular-nums">{t("Étape {n} sur {total}", { n: index + 1, total: STEPS.length })}</p>
        <h3 className="text-title leading-snug font-semibold text-ink">{t(STEPS[index]!.label)}</h3>
        <p className="mt-0.5 text-ui leading-snug text-ink-soft">{t(STEP_LEADS[step])}</p>
        <p className="mt-0.5 text-meta leading-snug text-ink-muted">{t(STEP_REQUIRED[step])}</p>
      </div>
    </div>
  );
}

/** Emblème d'un ENI : son symbole APP-6 (identité hostile, fonction de son type de forces dominant) dans une case. */
export function EnemyEmblem({
  type,
  size = "md",
}: {
  type: CustomEnemySheet["forces"]["type_dominant"] | null | undefined;
  size?: "sm" | "md" | "lg";
}) {
  const box = size === "lg" ? "size-14" : size === "md" ? "size-10" : "size-7";
  const symbol = size === "lg" ? 24 : size === "md" ? 16 : 11;
  return (
    <span className={clsx("flex shrink-0 items-center justify-center rounded-lg border border-line bg-canvas", box)} aria-hidden>
      <UnitSymbol sidc={enemySidc(type)} size={symbol} />
    </span>
  );
}
